"use client"

import * as React from "react"
import Link from "next/link"
import Image from "next/image"
import { ArrowLeft, ArrowRight, CheckCircle2, Quote, Users, Bot, Globe, Ship, Truck, Navigation, Target, Clock, FileCheck, Plane } from "lucide-react"
import { Button, Badge } from "@sarkiai/ui"
import { useTranslations } from "@/components/I18nProvider"

export default function STDSACaseStudyClient() {
  const t = useTranslations("stdsa_page")

  return (
    <main className="flex min-h-screen flex-col">
      {/* Back link */}
      <div className="container mx-auto pt-8 px-4 lg:px-12">
        <Link
          href="/use-cases"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t("hero.back")}
        </Link>
      </div>

      {/* Hero Section with Port Background */}
      <section className="relative py-16 md:py-32 overflow-hidden mt-8 px-4 lg:px-12">
        <div className="absolute inset-0 z-0">
          <Image
            src="/assets/use-cases/stdsa/hero/port.png"
            alt="Port de commerce"
            fill
            className="object-cover opacity-60 scale-105"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/40 to-background" />
        </div>

        <div className="container relative z-10 mx-auto">
          <div className="max-w-5xl mx-auto space-y-12">
            <div className="flex flex-col md:flex-row md:items-center gap-10">
              <div className="w-32 h-32 relative rounded-3xl bg-white p-4 shrink-0 transition-transform duration-500">
                <Image
                  src="/assets/use-cases/stdsa/logo.png"
                  alt="STD SA Logo"
                  fill
                  className="object-contain p-4"
                />
              </div>
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <Badge className="bg-black text-white dark:bg-white dark:text-black border-none rounded-full px-4 py-1.5 text-[10px] uppercase tracking-widest">{t("hero.badge1")}</Badge>
                  <Badge variant="outline" className="border-black/20 dark:border-white/20 rounded-full px-4 py-1.5 text-[10px] uppercase tracking-widest">{t("hero.badge2")}</Badge>
                  <Badge variant="outline" className="border-black/20 dark:border-white/20 rounded-full px-4 py-1.5 text-[10px] uppercase tracking-widest">{t("hero.badge3")}</Badge>
                </div>
                <h1 className="text-4xl md:text-5xl uppercase tracking-tighter leading-none">
                  {t("hero.title")}
                </h1>
              </div>
            </div>

            <p className="text-lg md:text-2xl leading-tight max-w-3xl opacity-80 uppercase tracking-tight">
              {t("hero.subtitle")}
            </p>

            {/* Regional Expertise */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-12">
              <div className="flex items-center gap-6 p-6 rounded-[2rem] bg-white/50 dark:bg-card/50 backdrop-blur-xl border border-black/5 dark:border-white/10 shadow-xl shadow-black/5">
                <div className="w-12 h-12 rounded-2xl bg-black dark:bg-white flex items-center justify-center shrink-0 shadow-lg">
                  <Ship className="h-6 w-6 text-white dark:text-black" />
                </div>
                <div>
                  <div className="uppercase tracking-tighter text-lg">{t("hero.hub_title")}</div>
                  <div className="text-[10px] uppercase tracking-widest opacity-60">{t("hero.hub_desc")}</div>
                </div>
              </div>
              <div className="flex items-center gap-6 p-6 rounded-[2rem] bg-white/50 dark:bg-card/50 backdrop-blur-xl border border-black/5 dark:border-white/10 shadow-xl shadow-black/5">
                <div className="w-12 h-12 rounded-2xl bg-black dark:bg-white flex items-center justify-center shrink-0 shadow-lg">
                  <Truck className="h-6 w-6 text-white dark:text-black" />
                </div>
                <div>
                  <div className="uppercase tracking-tighter text-lg">{t("hero.corridors_title")}</div>
                  <div className="text-[10px] uppercase tracking-widest opacity-60">{t("hero.corridors_desc")}</div>
                </div>
              </div>
              <div className="flex items-center gap-6 p-6 rounded-[2rem] bg-white/50 dark:bg-card/50 backdrop-blur-xl border border-black/5 dark:border-white/10 shadow-xl shadow-black/5">
                <div className="w-12 h-12 rounded-2xl bg-black dark:bg-white flex items-center justify-center shrink-0 shadow-lg">
                  <Globe className="h-6 w-6 text-white dark:text-black" />
                </div>
                <div>
                  <div className="uppercase tracking-tighter text-lg">{t("hero.region_title")}</div>
                  <div className="text-[10px] uppercase tracking-widest opacity-60">{t("hero.region_desc")}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Paragraph */}
      <section className="py-16 md:py-24 bg-white dark:bg-black px-4 lg:px-12">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto space-y-12">
            <h2 className="text-2xl md:text-4xl  uppercase tracking-tighter leading-none">{t("intro.title")}</h2>
            <div className="prose prose-xl dark:prose-invert max-w-none text-muted-foreground space-y-8">
              <p className="leading-relaxed first-letter:text-5xl first-letter: first-letter:mr-4 first-letter:float-left first-letter:text-foreground">
                {t("intro.p1")}
              </p>
              <p className="leading-relaxed font-medium">
                {t("intro.p2")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services with Images Section */}
      <section className="py-20 md:py-32 text-foreground relative overflow-hidden px-4 lg:px-12">
        <div className="absolute inset-0 bg-white dark:bg-black" />
        <div className="container relative z-10 mx-auto">
          <div className="max-w-6xl mx-auto space-y-24">
            <div className="text-center space-y-6 max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-5xl  uppercase tracking-tighter leading-none">{t("expertise.title")}</h2>
              <p className="text-lg md:text-xl opacity-60">{t("expertise.subtitle")}</p>
            </div>

            {/* Service 1: Air */}
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div className="space-y-10">
                <div className="w-16 h-16 rounded-[1.5rem] bg-black dark:bg-white text-white dark:text-black flex items-center justify-center shadow-2xl">
                  <Plane className="h-8 w-8" />
                </div>
                <div className="space-y-6">
                  <h3 className="text-4xl md:text-5xl  uppercase tracking-tighter leading-none">{t("expertise.air.title")}</h3>
                  <p className="text-xl opacity-70 leading-relaxed">
                    {t("expertise.air.content")}
                  </p>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-center gap-4 text-lg">
                    <div className="w-6 h-6 rounded-full bg-black dark:bg-white text-white dark:text-black flex items-center justify-center shrink-0">
                      <CheckCircle2 className="h-4 w-4" />
                    </div>
                    {t("expertise.air.check1")}
                  </li>
                  <li className="flex items-center gap-4 text-lg">
                    <div className="w-6 h-6 rounded-full bg-black dark:bg-white text-white dark:text-black flex items-center justify-center shrink-0">
                      <CheckCircle2 className="h-4 w-4" />
                    </div>
                    {t("expertise.air.check2")}
                  </li>
                </ul>
              </div>
              <div className="relative h-[500px] md:h-[600px] rounded-4xl overflow-hidden shadow-2xl group">
                <Image
                  src="/assets/use-cases/stdsa/services/air-freight.jpg"
                  alt="Transport Aérien"
                  fill
                  className="object-cover group-hover:scale-110 transition-transform duration-1000"
                />
                <div className="absolute inset-0 bg-black/5 dark:bg-black/40 group-hover:bg-transparent transition-colors duration-700" />
              </div>
            </div>

            {/* Service 2: Road */}
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div className="relative h-[500px] md:h-[600px] rounded-4xl overflow-hidden shadow-2xl md:order-last group">
                <Image
                  src="/assets/use-cases/stdsa/services/road-freight.jpg"
                  alt="Transport Routier"
                  fill
                  className="object-cover group-hover:scale-110 transition-transform duration-1000"
                />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors duration-700" />
              </div>
              <div className="space-y-10">
                <div className="w-16 h-16 rounded-[1.5rem] bg-black dark:bg-white text-white dark:text-black flex items-center justify-center shadow-2xl">
                  <Truck className="h-8 w-8" />
                </div>
                <div className="space-y-6">
                  <h3 className="text-4xl md:text-5xl  uppercase tracking-tighter leading-none">{t("expertise.road.title")}</h3>
                  <p className="text-xl opacity-70 leading-relaxed">
                    {t("expertise.road.content")}
                  </p>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-center gap-4 text-lg">
                    <div className="w-6 h-6 rounded-full bg-black dark:bg-white text-white dark:text-black flex items-center justify-center shrink-0">
                      <CheckCircle2 className="h-4 w-4" />
                    </div>
                    {t("expertise.road.check1")}
                  </li>
                  <li className="flex items-center gap-4 text-lg">
                    <div className="w-6 h-6 rounded-full bg-black dark:bg-white text-white dark:text-black flex items-center justify-center shrink-0">
                      <CheckCircle2 className="h-4 w-4" />
                    </div>
                    {t("expertise.road.check2")}
                  </li>
                </ul>
              </div>
            </div>

            {/* Service 3: Rail */}
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div className="space-y-10">
                <div className="w-16 h-16 rounded-[1.5rem] bg-black dark:bg-white text-white dark:text-black flex items-center justify-center shadow-2xl">
                  <Navigation className="h-8 w-8" />
                </div>
                <div className="space-y-6">
                  <h3 className="text-4xl md:text-5xl  uppercase tracking-tighter leading-none">{t("expertise.rail.title")}</h3>
                  <p className="text-xl opacity-70 leading-relaxed">
                    {t("expertise.rail.content")}
                  </p>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-center gap-4 text-lg">
                    <div className="w-6 h-6 rounded-full bg-black dark:bg-white text-white dark:text-black flex items-center justify-center shrink-0">
                      <CheckCircle2 className="h-4 w-4" />
                    </div>
                    {t("expertise.rail.check1")}
                  </li>
                  <li className="flex items-center gap-4 text-lg">
                    <div className="w-6 h-6 rounded-full bg-black dark:bg-white text-white dark:text-black flex items-center justify-center shrink-0">
                      <CheckCircle2 className="h-4 w-4" />
                    </div>
                    {t("expertise.rail.check2")}
                  </li>
                </ul>
              </div>
              <div className="relative h-[500px] md:h-[600px] rounded-4xl overflow-hidden shadow-2xl group">
                <Image
                  src="/assets/use-cases/stdsa/services/rail-freight.jpg"
                  alt="Transport Ferroviaire"
                  fill
                  className="object-cover group-hover:scale-110 transition-transform duration-1000"
                />
                <div className="absolute inset-0 bg-black/5 dark:bg-black/40 group-hover:bg-transparent transition-colors duration-700" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Challenge Section */}
      <section className="py-20 md:py-32 bg-white dark:bg-black px-4 lg:px-12">
        <div className="container mx-auto">
          <div className="max-w-5xl mx-auto space-y-16">
            <div className="flex flex-col items-center text-center space-y-6">
              <div className="w-16 h-16 rounded-2xl bg-black/5 dark:bg-white/10 flex items-center justify-center shadow-xl">
                <Target className="h-8 w-8 text-black dark:text-white" />
              </div>
              <h2 className="text-3xl md:text-6xl  uppercase tracking-tighter">{t("challenges.title")}</h2>
            </div>

            <div className="grid md:grid-cols-3 gap-12">
              <div className="space-y-6 p-10 rounded-[2.5rem] bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 group hover:scale-105 transition-transform">
                <div className="w-14 h-14 rounded-2xl bg-black dark:bg-white flex items-center justify-center text-white dark:text-black shadow-lg">
                  <Globe className="h-7 w-7" />
                </div>
                <h4 className="text-2xl  uppercase tracking-tight">{t("challenges.lang_title")}</h4>
                <p className="opacity-70 leading-relaxed">{t("challenges.lang_desc")}</p>
              </div>
              <div className="space-y-6 p-10 rounded-[2.5rem] bg-black dark:bg-white text-white dark:text-black shadow-2xl scale-110 relative z-10 border-none">
                <div className="w-14 h-14 rounded-2xl bg-white/20 dark:bg-black/10 flex items-center justify-center text-white dark:text-black shadow-lg">
                  <Clock className="h-7 w-7" />
                </div>
                <h4 className="text-2xl uppercase tracking-tight">{t("challenges.time_title")}</h4>
                <p className="opacity-70 leading-relaxed uppercase tracking-tight">{t("challenges.time_desc")}</p>
              </div>
              <div className="space-y-6 p-10 rounded-[2.5rem] bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 group hover:scale-105 transition-transform">
                <div className="w-14 h-14 rounded-2xl bg-black dark:bg-white flex items-center justify-center text-white dark:text-black shadow-lg">
                  <FileCheck className="h-7 w-7" />
                </div>
                <h4 className="text-2xl  uppercase tracking-tight">{t("challenges.customs_title")}</h4>
                <p className="opacity-70 leading-relaxed">{t("challenges.customs_desc")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 md:py-32 bg-black text-foreground rounded-t-[3rem] mx-0 overflow-hidden relative px-4 lg:px-12">
        <div className="absolute inset-0 bg-white dark:bg-black" />
        <div className="container relative z-10 mx-auto">
          <div className="max-w-5xl mx-auto space-y-16">
            <div className="space-y-8 max-w-3xl">
              <Badge className="bg-black dark:bg-white text-white dark:text-black rounded-full px-6 py-2 text-xs  uppercase tracking-widest border-none">{t("solution.badge")}</Badge>
              <h2 className="text-4xl md:text-5xl  uppercase tracking-tighter leading-none">{t("solution.title")}</h2>
              <p className="text-lg md:text-xl opacity-70 leading-relaxed">
                {t("solution.subtitle")}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              <div className="p-12 rounded-4xl bg-background border border-black/10 dark:border-white/10 space-y-8 group hover:bg-black/5 dark:hover:bg-white/5 transition-colors">
                <div className="w-20 h-20 rounded-[2rem] bg-black dark:bg-white text-white dark:text-black flex items-center justify-center shadow-2xl">
                  <Bot className="h-10 w-10" />
                </div>
                <div className="space-y-4">
                  <h3 className="text-3xl  uppercase tracking-tight">{t("solution.omni_title")}</h3>
                  <p className="text-lg opacity-60 leading-relaxed">{t("solution.omni_desc")}</p>
                </div>
              </div>
              <div className="p-12 rounded-4xl bg-background border border-black/10 dark:border-white/10 space-y-8 group hover:bg-black/5 dark:hover:bg-white/5 transition-colors">
                <div className="w-20 h-20 rounded-[2rem] bg-black dark:bg-white text-white dark:text-black flex items-center justify-center shadow-2xl">
                  <Users className="h-10 w-10" />
                </div>
                <div className="space-y-4">
                  <h3 className="text-3xl  uppercase tracking-tight">{t("solution.lang_title")}</h3>
                  <p className="text-lg opacity-60 leading-relaxed">{t("solution.lang_desc")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-24 md:py-32 bg-white dark:bg-background px-4 lg:px-12">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center space-y-16">
            <div className="relative inline-block">
              <Quote className="h-24 w-24 text-black/5 dark:text-white/5 absolute -top-12 -left-12 -scale-x-100" />
              <blockquote className="text-3xl md:text-5xl  tracking-tighter uppercase leading-tight text-foreground relative z-10">
                "{t("testimonial.quote")}"
              </blockquote>
            </div>
            <div className="flex flex-col items-center gap-6">
              <div className="w-24 h-24 rounded-[2rem] bg-black/5 dark:bg-white/10 relative overflow-hidden shadow-2xl border border-black/5 dark:border-white/10 p-4">
                <Image
                  src="/assets/use-cases/stdsa/logo.png"
                  alt="Direction STD SA"
                  fill
                  className="object-contain p-4"
                />
              </div>
              <div className="space-y-1">
                <div className="text-2xl uppercase tracking-tighter">{t("testimonial.author")}</div>
                <div className="text-[10px] uppercase tracking-widest opacity-40">{t("testimonial.company")}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-black text-foreground relative overflow-hidden px-4 lg:px-12">
        <div className="absolute inset-x-0 top-0 h-px bg-black/10 dark:bg-white/10" />
        <div className="absolute inset-0 bg-white dark:bg-black" />

        <div className="container relative z-10 mx-auto text-center space-y-12">
          <div className="space-y-6 max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-8xl  uppercase tracking-tighter leading-none">
              {t("cta.title")}
            </h2>
            <p className="text-lg md:text-xl opacity-60 max-w-3xl mx-auto leading-relaxed">
              {t("cta.subtitle")}
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-6 pt-8">
            <Button size="lg" className="px-10 rounded-2xl text-xs  uppercase tracking-widest bg-black dark:bg-white text-white dark:text-black hover:bg-black/90 dark:hover:bg-white/90 shadow-2xl" asChild>
              <Link href="/contact">
                {t("cta.expert")}
                <ArrowRight className="ml-3 h-5 w-5" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="px-10 rounded-2xl text-xs  uppercase tracking-widest border-black/20 dark:border-white/20 hover:bg-black/5 hover:text-black dark:hover:bg-white/5 dark:hover:text-white" asChild>
              <Link href="/enterprise">
                {t("cta.discover")}
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </main>
  )
}
